/**
 * S23 — imperative action handlers for the Object Explorer "Dashboards" node
 * (double-click/Enter activation + its context menu: Open/Rename/Duplicate/
 * Delete). Every mutation routes through the S1 store -> main-process
 * repository (the sole writer; licensing enforced there — never a Pro flag
 * from here). Kept separate from the pure `dashboardExplorerNode.ts` builder
 * since these call the store, toasts, and the Object Explorer confirm-dialog
 * store; also kept OUT of `hooks/useDashboardActions.ts` (off-limits for this
 * slice, and that hook is scoped to a single open dashboard TAB, not a tree row).
 */
import { toast } from '@/components/ui/toaster';
import { useConfirmDialogStore } from '@/features/database-objects/services/confirmDialogStore';
import { useDashboardStore } from '../store/dashboardStore';
import { openDashboardTab } from '../utils/openDashboardTab';
import { usePresentRequestStore } from '../store/presentRequestStore';
import { exportDashboardDefinition } from '../services/dashboardExportService';
import { FREE_DASHBOARD_LIMIT, type Dashboard, type RepoResult } from '../types/dashboardTypes';

function track(name: string, props?: Record<string, string | number | boolean | null>): void {
  window.electronAPI?.analytics?.track(name, props).catch(() => {});
}

/** Double-click/Enter on a child row, or the "Open" context-menu item. */
export function openDashboardFromExplorer(dashboardId: string): void {
  openDashboardTab(dashboardId);
  track('feature:dashboard_open', { from: 'object-explorer' });
}

/**
 * "Present" context-menu item (S13): opens/focuses the dashboard tab, then
 * drops a request into `presentRequestStore` — DashboardTab consumes it and
 * enters Present Mode once it resolves the matching dashboard. Analytics
 * fires exactly once, from `useDashboardPresent.enterPresent` (the single
 * place BOTH entry points — this one and the tab's own kebab — converge).
 */
export function presentDashboardFromExplorer(dashboardId: string): void {
  openDashboardTab(dashboardId);
  usePresentRequestStore.getState().request(dashboardId);
}

/**
 * "Export definition…" context-menu item (S14): saves the canonical
 * definition JSON. Analytics fires on success inside the service.
 */
export async function exportDashboardDefinitionFromExplorer(dashboardId: string): Promise<void> {
  const dashboard = useDashboardStore.getState().dashboards.find((d) => d.id === dashboardId);
  if (!dashboard) return;
  const outcome = await exportDashboardDefinition(dashboard);
  if (!outcome.ok && !outcome.cancelled) {
    toast.error({ title: 'Export failed', description: outcome.error });
  }
}

const MAX_RENAME_REPLAYS = 3;

/**
 * Full-document save as a rebasable intent (same shape as
 * `useDashboardActions.saveWithReplay`, duplicated in isolation here since
 * this module may not import from `hooks/`): on `conflict` the store has
 * already reloaded the mirror, so re-apply the rename to the fresh revision
 * and retry (bounded).
 */
export async function renameDashboardFromExplorer(
  dashboardId: string,
  name: string,
): Promise<RepoResult<Dashboard>> {
  const result = await saveIntentFromExplorer(dashboardId, 'rename dashboard', (d) => ({
    ...d,
    name,
  }));
  if (result.ok) track('feature:dashboard_rename');
  return result;
}

/** S10 — the shared settings dialog also edits parameters from here. */
export async function saveDashboardParametersFromExplorer(
  dashboardId: string,
  parameters: Dashboard['parameters'],
): Promise<RepoResult<Dashboard>> {
  return saveIntentFromExplorer(dashboardId, 'save parameters', (d) => ({ ...d, parameters }));
}

/** S13 — the shared settings dialog also edits the playlist from here. */
export async function saveDashboardPlaylistFromExplorer(
  dashboardId: string,
  playlist: Dashboard['playlist'],
): Promise<RepoResult<Dashboard>> {
  return saveIntentFromExplorer(dashboardId, 'save the playlist', (d) => ({ ...d, playlist }));
}

async function saveIntentFromExplorer(
  dashboardId: string,
  intentLabel: string,
  apply: (d: Dashboard) => Dashboard,
): Promise<RepoResult<Dashboard>> {
  const store = useDashboardStore;
  let target = store.getState().dashboards.find((d) => d.id === dashboardId);
  if (!target) return { ok: false, error: 'not-found' };

  for (let attempt = 0; attempt < MAX_RENAME_REPLAYS; attempt++) {
    const result = await store.getState().save(apply(target), target.revision);
    if (result.ok) return result;
    if (result.error !== 'conflict') {
      toast.error({ title: `Could not ${intentLabel}`, description: result.detail });
      return result;
    }
    const fresh = store.getState().dashboards.find((d) => d.id === dashboardId);
    if (!fresh) return result; // deleted concurrently — nothing to replay onto
    target = fresh;
  }
  toast.error({
    title: `Could not ${intentLabel}`,
    description: 'The dashboard kept changing while saving. Try again.',
  });
  return { ok: false, error: 'conflict' };
}

/** "Duplicate" context-menu item. The repository enforces the free cap. */
export async function duplicateDashboardFromExplorer(dashboardId: string): Promise<void> {
  const result = await useDashboardStore.getState().duplicate(dashboardId);
  if (result.ok) {
    openDashboardTab(result.value.id);
    track('feature:dashboard_duplicate');
    return;
  }
  if (result.error === 'free-limit') {
    toast.error({
      title: 'Free plan limit reached',
      description:
        result.detail ?? `Free plan is limited to ${FREE_DASHBOARD_LIMIT} dashboards. Upgrade to Pro for unlimited.`,
    });
    return;
  }
  toast.error({ title: 'Could not duplicate dashboard', description: result.detail });
}

/** "Delete" context-menu item — confirms first (repo rule: destructive actions confirm). */
export async function deleteDashboardFromExplorer(dashboardId: string): Promise<void> {
  const dashboard = useDashboardStore.getState().dashboards.find((d) => d.id === dashboardId);
  if (!dashboard) return;

  const confirmed = await useConfirmDialogStore
    .getState()
    .requestConfirm(
      `Delete "${dashboard.name}"?`,
      'This removes the dashboard definition from this machine. Tiles and their queries are not recoverable.',
      'Delete',
    );
  if (!confirmed) return;

  const result = await useDashboardStore.getState().remove(dashboardId);
  if (result.ok) {
    track('feature:dashboard_delete');
    return;
  }
  toast.error({ title: 'Could not delete dashboard', description: result.detail });
}
